import React, { useMemo } from 'react'
import { Formik, FormikHelpers } from 'formik'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'
import _isNil from 'lodash/isNil'

import {
  StyledApplyPromocodeBtn,
  StyledPromocodeApplied,
  StyledPromocodeField,
  StyledPromocodeForm,
  StyledPromocodeInputWrap,
} from './styles'
import { getInvoiceHasAutoAppliedCoupon } from 'features/home/components/HomeSubscription/utils'
import { upcomingInvoiceSelectors } from 'features/home/components/HomeSubscription/selectors'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useHomeRouteParams } from 'features/home/hooks'
import { errorCode, promocodeFieldError } from './consts'
import { promocodeMessages } from './messages'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { getInvoiceThunk } from '../../slice'

interface PromocodeFormValues {
  promocode: string
}

interface PromocodeFormProps {
  priceId: string
}

const PromocodeForm: React.FC<PromocodeFormProps> = (props) => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { homeId } = useHomeRouteParams()

  const upcomingInvoice = useAppSelector(upcomingInvoiceSelectors.data)
  const currentRootDomainId = useAppSelector(currentRootDomainIdSelector)

  const initialValues = {
    promocode: '',
  }

  const invoiceHasAutoAppliedCoupon = useMemo(
    () => getInvoiceHasAutoAppliedCoupon(upcomingInvoice),
    [upcomingInvoice]
  )

  const handleSubmit = async (
    values: PromocodeFormValues,
    formikHelpers: FormikHelpers<PromocodeFormValues>
  ) => {
    if (homeId && currentRootDomainId) {
      const response = await dispatch(
        getInvoiceThunk(
          { priceId: props.priceId, promocode: values.promocode },
          homeId,
          currentRootDomainId
        )
      )

      if (_isNil(response)) {
        formikHelpers.setFieldError(
          'promocode',
          formatMessage(promocodeFieldError.E_COUPON_INVALID)
        )
        return
      }

      const { promoCodeErrors } = response

      if (promoCodeErrors.length) {
        const [error] = promoCodeErrors
        const errorMessage = promocodeFieldError[error.code as errorCode]
        if (errorMessage)
          formikHelpers.setFieldError('promocode', formatMessage(errorMessage))
      } else {
        formikHelpers.resetForm()
      }
    }
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        promocode: Yup.string().max(
          15,
          formatMessage(promocodeMessages.promocodeFieldTooLong)
        ),
      }),
    [formatMessage]
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {(formikProps) => {
        return (
          <StyledPromocodeForm>
            <StyledPromocodeInputWrap>
              <StyledPromocodeField
                name="promocode"
                placeholder={formatMessage(
                  promocodeMessages.promocodeFieldPlaceholder
                )}
                disabled={invoiceHasAutoAppliedCoupon}
              />
              {invoiceHasAutoAppliedCoupon && (
                <StyledPromocodeApplied>
                  {formatMessage(promocodeMessages.promocodeAlreadyApplied)}
                </StyledPromocodeApplied>
              )}
            </StyledPromocodeInputWrap>

            {
              <StyledApplyPromocodeBtn
                type="submit"
                variant="secondary"
                disabled={
                  !props.priceId ||
                  !formikProps.values.promocode ||
                  invoiceHasAutoAppliedCoupon
                }
              >
                {formatMessage(promocodeMessages.applyPromocode)}
              </StyledApplyPromocodeBtn>
            }
          </StyledPromocodeForm>
        )
      }}
    </Formik>
  )
}

export default PromocodeForm
